import { HttpContextToken, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

/**
 * HttpContext token to skip duplicate request cancellation
 * Usage: this.http.get(url, { context: new HttpContext().set(SKIP_DUPLICATE_CHECK, true) })
 */
export const SKIP_DUPLICATE_CHECK = new HttpContextToken<boolean>(() => false);

/**
 * Map to store pending requests
 * Key: unique request identifier (URL + method + params/body hash)
 * Value: Subject to cancel the request
 */
const pendingRequests = new Map<string, Subject<void>>();

/**
 * Creates a unique key for a request based on URL, method, and optionally body/params
 */
function createRequestKey(req: HttpRequest<any>): string {
  let key = `${req.method}:${req.urlWithParams}`;

  // Include body for POST/PUT/PATCH requests to differentiate them
  if (req.body && ['POST', 'PUT', 'PATCH'].includes(req.method)) {
    try {
      const bodyString = typeof req.body === 'string'
        ? req.body
        : JSON.stringify(req.body);
      // Create a simple hash of the body
      const bodyHash = bodyString.length > 200
        ? bodyString.substring(0, 200) + '_' + bodyString.length
        : bodyString;
      key += `:${bodyHash}`;
    } catch (e) {
      // If body can't be serialized, include a placeholder
      key += `:${typeof req.body}`;
    }
  }

  return key;
}

/**
 * Interceptor that cancels duplicate requests
 * If the same API is called multiple times, it cancels the previous request(s)
 * 
 * Usage:
 * - By default, all requests are checked for duplicates
 * - To skip duplicate check for a specific request:
 *   import { SKIP_DUPLICATE_CHECK } from '@insite-store360/auth';
 *   this.http.get(url, { context: new HttpContext().set(SKIP_DUPLICATE_CHECK, true) })
 */
export const duplicateRequestInterceptor: HttpInterceptorFn = (req, next) => {
  // Skip cancellation for requests that explicitly opt out
  if (req.context.get(SKIP_DUPLICATE_CHECK) === true) {
    return next(req);
  }

  const requestKey = createRequestKey(req);

  // Check if there's already a pending request with the same key
  const existingCancelSubject = pendingRequests.get(requestKey);

  if (existingCancelSubject && !existingCancelSubject.closed) {
    // Cancel the previous request by emitting to its cancel subject
    existingCancelSubject.next();
    existingCancelSubject.complete();
  }

  // Create a new cancel subject for this request
  const cancelSubject = new Subject<void>();
  pendingRequests.set(requestKey, cancelSubject);

  // Execute the request and cancel it if cancelSubject emits
  return next(req).pipe(
    takeUntil(cancelSubject),
    finalize(() => {
      // Clean up: remove the request from pending map when it completes
      const currentCancelSubject = pendingRequests.get(requestKey);
      if (currentCancelSubject === cancelSubject) {
        pendingRequests.delete(requestKey);
      }
    })
  );
};
